import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';

import type {
  ActiveWorkflowResponse,
  ApiResponse,
  ConnectedConnectorResponse,
  ConnectionHealthResult,
  ConnectionResponse,
  ConnectionsHealthResponse,
  ConnectionStatus,
  ConnectionType,
  ConnectionUpdateResponse,
  ConnectionValidationResponse,
  CreateConnectionRequest,
  GoogleAdsCustomer,
  GoogleAdsCustomersResponse,
  GoogleAdsOAuthStartResponse,
  PrimaryFeedsResponse,
  SupplementalDataSourcesResponse,
  UploadTargetsResponse,
} from '../types/connection.types';
import type {
  IntegrationPipeline,
  PipelineListRequest,
  PipelineListResponseData,
} from '../types/pipeline.types';

/** Create a connection. Live backend validates credentials against the
 *  platform API inside this same call. */
export async function createConnection(
  data: CreateConnectionRequest,
  subsidiaryId?: string,
): Promise<ConnectionResponse> {
  const res = await integrationsClient
    .post(endpoints.connections.create(), {
      json: data,
      // Optional page-local subsidiary scope; the interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionResponse>>();
  return res.data;
}

/** List a connector's file-upload destinations by direct merchant id, before
 *  any connection exists. Used by the onboarding SFTP target picker so the
 *  operator can bind `config.sftp_file_name` to a real datasource on create. */
export async function getUploadTargets(
  connectorCode: string,
  merchantId: string,
): Promise<UploadTargetsResponse> {
  const res = await integrationsClient
    .get(endpoints.connectors.uploadTargets(connectorCode), {
      searchParams: { merchantId },
    })
    .json<ApiResponse<UploadTargetsResponse>>();
  return res.data;
}

/** List an existing connection's file-upload destinations, resolved by its own
 *  handler. Unlike `getUploadTargets` (merchant-id addressed, GMC-shaped) this
 *  works for any connector that opts into upload delivery — Meta resolves its
 *  catalog's supplementary feed from `config.catalog_id`. */
export async function getConnectionUploadTargets(
  connectionId: string,
  subsidiaryId?: string,
): Promise<UploadTargetsResponse> {
  const res = await integrationsClient
    .get(endpoints.connections.uploadTargets(connectionId), {
      // Optional page-local subsidiary scope; the interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<UploadTargetsResponse>>();
  return res.data;
}

/** List the GMC supplemental data sources a translated locale can be delivered
 *  to. Addressed by the connection when one exists (works for own-OAuth merchants
 *  too), otherwise by `merchantId` through the platform identity (create flow). */
export async function getSupplementalDataSources(
  target: { connectionId: string } | { connectorCode: string; merchantId: string },
): Promise<SupplementalDataSourcesResponse> {
  const request =
    'connectionId' in target
      ? integrationsClient.get(endpoints.connections.supplementalDataSources(target.connectionId))
      : integrationsClient.get(endpoints.connectors.supplementalDataSources(target.connectorCode), {
          searchParams: { merchantId: target.merchantId },
        });
  const res = await request.json<ApiResponse<SupplementalDataSourcesResponse>>();
  return res.data;
}

/** List the primary data sources a GMC connection can pin ingestion to.
 *  Read-only — never writes the pin. Omit `accountId` on the first call; on an
 *  MCA the response lists sub-accounts and the caller repeats with one of them.
 *  The server rejects an `accountId` that isn't a child of this merchant. */
export async function getPrimaryFeeds(
  connectionId: string,
  accountId?: string | null,
  subsidiaryId?: string,
): Promise<PrimaryFeedsResponse> {
  const res = await integrationsClient
    .get(endpoints.connections.primaryFeeds(connectionId), {
      ...(accountId ? { searchParams: { accountId } } : {}),
      // Optional page-local subsidiary scope; the interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<PrimaryFeedsResponse>>();
  return res.data;
}

export async function getConnectedConnectors(
  subsidiaryId?: string,
): Promise<ConnectedConnectorResponse[]> {
  const res = await integrationsClient
    .get(endpoints.connections.connected(), {
      // Optional page-local subsidiary scope; the interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectedConnectorResponse[]>>();
  return res.data;
}

/** Resume a paused connection, flipping it back to ACTIVE so downstream syncs
 *  continue. Counterpart to `pauseConnection`; surfaced on the Connect
 *  Platforms cards when a connection is in the PAUSED state. */
export async function resumeConnection(
  id: string,
  subsidiaryId?: string,
): Promise<ConnectionResponse> {
  const res = await integrationsClient
    .post(endpoints.connections.resume(id), {
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionResponse>>();
  return res.data;
}

/** Full paginated connections listing (full records, incl. `type` + `status`).
 *
 *  Used in two ways:
 *  - `useCreateConnection` reactivation check — detect a non-ACTIVE row of the
 *    same type so we PATCH instead of duplicating.
 *  - The Connect Platforms screen — list ACTIVE + PAUSED connections so paused
 *    rows surface with a resume affordance (the connector-summary
 *    `/connections/connected` endpoint omits status filtering).
 *
 *  Pass `statuses` to filter (e.g. `['ACTIVE', 'PAUSED']`); omit for every
 *  status. Wide-open otherwise + a generous page size — only the first page is
 *  read; revisit if the per-subsidiary connection count is expected to exceed
 *  50. */
export async function listConnections(
  statuses?: ConnectionStatus[],
  subsidiaryId?: string,
  category?: string,
): Promise<ConnectionResponse[]> {
  // When a `category` is supplied (e.g. the Products dropdown, which only wants
  // shopping-feed / commerce-platform connectors) send the connector-category
  // filter shape: `{ status, type, category }` + a larger, sorted page. Absent
  // a category we keep the existing `{ statuses }` shape used app-wide.
  const filters = category
    ? { status: statuses?.[0] ?? null, type: null, category }
    : statuses?.length
      ? { statuses }
      : {};
  const res = await integrationsClient
    .post(endpoints.connections.list(), {
      json: {
        filters,
        pagination: category ? { page: 1, size: 100, sort_order: 'DESC' } : { page: 1, size: 50 },
      },
      // Optional page-local subsidiary scope (platform Catalog Images page).
      // The subsidiary interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionResponse[]>>();
  return res.data;
}

/** Fetch a single connection's full record, including its `config` payload.
 *  Used by the Meta hub to read `config.catalog_account` (catalog name +
 *  product count) — the `connected` list endpoint returns connector-summary
 *  rows only, not the connection config. */
export async function getConnection(
  id: string,
  subsidiaryId?: string,
): Promise<ConnectionResponse> {
  const res = await integrationsClient
    .get(endpoints.connections.byId(id), {
      // Optional page-local subsidiary scope; the interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionResponse>>();
  return res.data;
}

/** Live account-level metrics snapshot for a connection (impressions, product
 *  counts, etc.). The payload is connector-specific and rendered generically,
 *  so it's typed as an open record. The backend 501s for connectors without
 *  account metrics — callers gate the call by connector before invoking. */
export async function getConnectionMetrics(
  id: string,
  subsidiaryId?: string,
): Promise<Record<string, unknown>> {
  const res = await integrationsClient
    .get(endpoints.connections.metrics(id), {
      // Scope to the page-local subsidiary when the Connections page overrides
      // it; the interceptor leaves an explicitly-set header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<Record<string, unknown>>>();
  return res.data;
}

/** Look up the connection's currently-active pipeline workflow (defaults to
 *  the INGESTION pipeline — the catalog import). Returns `null` when the
 *  backend 404s, which is its "nothing running" signal: the onboarding hub
 *  uses a non-null result to resume Step 3 rather than restart the flow.
 *  Other errors (400 connector mismatch, 409 drift) propagate to the caller. */
export async function getConnectionActiveWorkflow(
  id: string,
  connectorCode: ConnectionType,
): Promise<ActiveWorkflowResponse | null> {
  try {
    const res = await integrationsClient
      .get(endpoints.connections.activeWorkflow(id), {
        searchParams: { connector_code: connectorCode, pipeline_type: 'INGESTION' },
      })
      .json<ApiResponse<ActiveWorkflowResponse>>();
    return res.data;
  } catch (err) {
    // 404 = no active INGESTION run for this connection. Expected when the
    // import already finished or never started — not an error, so report null.
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}

/** Aggregate live health check across every connection in the subsidiary.
 *  Fired once on the Connect Platforms screen to badge each connected card as
 *  healthy/unhealthy; the per-connection `isValid` is mapped by `connectionId`. */
export async function getConnectionsHealth(
  subsidiaryId?: string,
): Promise<ConnectionsHealthResponse> {
  const res = await integrationsClient
    .post(endpoints.connections.health(), {
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionsHealthResponse>>();
  return res.data;
}

/** Live health check for a single connection, with a per-component breakdown.
 *  Fetched lazily when the user hovers an unhealthy connection's heart to see
 *  what specifically failed. */
export async function getConnectionHealth(
  id: string,
  subsidiaryId?: string,
): Promise<ConnectionHealthResult> {
  const res = await integrationsClient
    .post(endpoints.connections.healthById(id), {
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionHealthResult>>();
  return res.data;
}

/** Re-fetch platform account info for an existing connection — used by the
 *  confirmation screens to surface campaign counts, report suites, etc. */
export async function validateConnection(
  id: string,
  subsidiaryId?: string,
): Promise<ConnectionValidationResponse> {
  const res = await integrationsClient
    .post(endpoints.connections.validate(id), {
      // Optional page-local subsidiary scope; the interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionValidationResponse>>();
  return res.data;
}

/** Body shape for `PATCH /connections/{id}`. All fields optional; backend
 *  merges with the existing row. Mirrors `CreateConnectionRequest` (minus
 *  `type` — the URL keys the row) plus `status` for explicit lifecycle
 *  transitions (e.g. reactivating a PAUSED row) and `delivery_method` for
 *  connectors that surface that field at the top level on the wire (GMC,
 *  Shopify). Open to extra string-valued top-level fields so future
 *  connectors can mirror their response shape without churning this type.
 *
 *  Note (integrations PR #182): `delivery_method` is now cross-field-validated
 *  on every PATCH. Pass `credentials: { SFTP: null }` alongside
 *  `delivery_method: 'API'` to clear stale SFTP creds when switching back. */
export interface UpdateConnectionRequest {
  type?: ConnectionType;
  name?: string;
  config?: Record<string, unknown>;
  credentials?: Record<string, unknown>;
  status?: ConnectionStatus;
  delivery_method?: string;
  [extra: string]: unknown;
}

/** Partially update a connection. Used after the wizard's picker screen to
 *  persist the chosen `property_id` into `config`; also used by
 *  `useCreateConnection`'s reactivation branch to flip a non-ACTIVE row
 *  back to ACTIVE with refreshed credentials. */
export async function updateConnection(
  id: string,
  body: UpdateConnectionRequest,
  subsidiaryId?: string,
): Promise<ConnectionUpdateResponse> {
  const res = await integrationsClient
    .patch(endpoints.connections.update(id), {
      json: body,
      // Optional page-local subsidiary scope; the interceptor leaves an explicit header untouched.
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionUpdateResponse>>();
  return res.data;
}

/** Delete a connection. Destructive; backend emits a DELETED event and
 *  unblocks the one-connection-per-connector uniqueness constraint so a
 *  follow-up create with fresh credentials can succeed. */
export async function deleteConnection(id: string, subsidiaryId?: string): Promise<void> {
  await integrationsClient.delete(endpoints.connections.delete(id), {
    ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
  });
}

/** Pause an active connection. Used as the recovery path when DELETE is
 *  rejected with INTEGRITY_ERROR (a pipeline is still bound to the
 *  connection); pausing leaves the connection record intact but halts
 *  downstream syncs. */
export async function pauseConnection(
  id: string,
  subsidiaryId?: string,
): Promise<ConnectionResponse> {
  const res = await integrationsClient
    .post(endpoints.connections.pause(id), {
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionResponse>>();
  return res.data;
}

/** Start the Google Ads interactive OAuth flow for a `PENDING` connection
 *  created without a `refresh_token` (advance mode, GOOGLE_ADS only). Returns
 *  the Google consent-screen URL to redirect the browser to. `redirect_uri`
 *  must be byte-identical to the one passed to `completeConnectionOAuth`, and
 *  registered on the tenant's own OAuth client in Google Cloud Console. */
export async function startConnectionOAuth(
  id: string,
  redirectUri: string,
  subsidiaryId?: string,
): Promise<GoogleAdsOAuthStartResponse> {
  const res = await integrationsClient
    .post(endpoints.connections.oauthStart(id), {
      searchParams: { redirect_uri: redirectUri },
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<GoogleAdsOAuthStartResponse>>();
  return res.data;
}

/** Dedup cache for `completeConnectionOAuth`, keyed by the single-use `code` —
 *  mirrors `exchangeOAuthCode`'s `pendingExchanges` in `auth.service.ts` (see
 *  docs/changes/2026-08-12-oauth-double-code-exchange-login-failure-rca.md).
 *  A `useEffect` re-run (StrictMode, an i18next namespace landing mid-request
 *  and changing `t`'s identity, etc.) must never replay this call — Google's
 *  `code` is one-time-use and a second exchange 400s. The entry is kept on
 *  success (the code is spent regardless) and dropped only on failure, so a
 *  genuine user-initiated retry (fresh `code` from a new `oauth/start`) isn't
 *  blocked. */
const pendingOAuthCompletions = new Map<string, Promise<ConnectionResponse>>();

/** Exchange the Google-redirect `code` for tokens, activating the connection.
 *  Returns the full `ConnectionResponse` (`status: 'ACTIVE'` on success). On
 *  failure the connection is left untouched at `PENDING` — safe to retry with
 *  a fresh `code` via `startConnectionOAuth`. */
export async function completeConnectionOAuth(
  id: string,
  code: string,
  redirectUri: string,
  subsidiaryId?: string,
): Promise<ConnectionResponse> {
  const existing = pendingOAuthCompletions.get(code);
  if (existing) return existing;

  const promise = integrationsClient
    .post(endpoints.connections.oauthCallback(id), {
      searchParams: { code, redirect_uri: redirectUri },
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<ConnectionResponse>>()
    .then((res) => res.data);

  promise.catch(() => pendingOAuthCompletions.delete(code));
  pendingOAuthCompletions.set(code, promise);
  return promise;
}

/** List the Google Ads accounts the signed-in user can access — connect
 *  mode's account picker, once step 4 (oauth/callback) has completed for this
 *  connection. */
export async function getGoogleAdsCustomers(
  id: string,
  subsidiaryId?: string,
): Promise<GoogleAdsCustomer[]> {
  const res = await integrationsClient
    .get(endpoints.connections.googleAdsCustomers(id), {
      ...(subsidiaryId ? { headers: { 'X-Subsidiary-ID': subsidiaryId } } : {}),
    })
    .json<ApiResponse<GoogleAdsCustomersResponse>>();
  return res.data.customers;
}

/** Paginated pipeline list. Body is wide-open (no filters) by default and
 *  the page size is generous so the disconnect-error fallback gets the
 *  full active set in one round-trip — only used to look up whether any
 *  pipeline references the connector code we just failed to delete. */
export async function listPipelines(
  body: PipelineListRequest = { filters: {}, pagination: { page: 1, size: 100 } },
): Promise<IntegrationPipeline[]> {
  const res = await integrationsClient
    .post(endpoints.integration.pipelinesList(), { json: body })
    .json<ApiResponse<PipelineListResponseData>>();
  return res.data.pipelines;
}
